import * as L from 'leaflet';

/** Whether a GeoJSON Polygon/MultiPolygon overlaps a box or lasso path. */
export function areaIntersectsPath(
  boundaryGeoJson: string,
  path: readonly L.LatLng[],
): boolean {
  if (path.length < 3) return false;
  return areaRingsIntersectPath(polygonRings(boundaryGeoJson), path);
}

/** Tests the rings currently rendered by Leaflet, without reparsing a DTO. */
export function areaRingsIntersectPath(
  polygons: readonly (readonly (readonly L.LatLng[])[])[],
  path: readonly L.LatLng[],
): boolean {
  if (path.length < 3) return false;
  return polygons.some(rings => {
    const outer = rings[0];
    if (!outer?.length) return false;

    if (outer.some(point => pointInPath(point, path))) return true;
    if (path.some(point => pointInPolygonRings(point, rings))) return true;
    return rings.some(ring => edgesIntersect(ring, path));
  });
}

export function boundsPath(bounds: L.LatLngBounds): L.LatLng[] {
  return [
    L.latLng(bounds.getNorth(), bounds.getWest()),
    L.latLng(bounds.getNorth(), bounds.getEast()),
    L.latLng(bounds.getSouth(), bounds.getEast()),
    L.latLng(bounds.getSouth(), bounds.getWest()),
  ];
}

/** Whether two simple polygon paths overlap or contain one another. */
export function pathsIntersect(
  left: readonly L.LatLng[],
  right: readonly L.LatLng[],
): boolean {
  if (left.length < 3 || right.length < 3) return false;
  return left.some(point => pointInPath(point, right))
    || right.some(point => pointInPath(point, left))
    || edgesIntersect(left, right);
}

function polygonRings(boundaryGeoJson: string): L.LatLng[][][] {
  try {
    const geometry = JSON.parse(boundaryGeoJson) as {
      type?: string;
      coordinates?: number[][][] | number[][][][];
    };
    const polygons = geometry.type === 'Polygon'
      ? [geometry.coordinates as number[][][]]
      : geometry.type === 'MultiPolygon'
        ? geometry.coordinates as number[][][][]
        : [];
    return polygons.map(polygon => polygon.map(ring =>
      ring.map(([lng, lat]) => L.latLng(lat, lng))));
  } catch {
    return [];
  }
}

function pointInPath(point: L.LatLng, path: readonly L.LatLng[]): boolean {
  let inside = false;
  for (let i = 0, j = path.length - 1; i < path.length; j = i++) {
    const a = path[i];
    const b = path[j];
    if ((a.lat > point.lat) === (b.lat > point.lat)) continue;
    const crossing = (b.lng - a.lng) * (point.lat - a.lat)
      / (b.lat - a.lat) + a.lng;
    if (point.lng < crossing) inside = !inside;
  }
  return inside;
}

function pointInPolygonRings(
  point: L.LatLng,
  rings: readonly (readonly L.LatLng[])[],
): boolean {
  return !!rings[0] && pointInPath(point, rings[0])
    && !rings.slice(1).some(hole => pointInPath(point, hole));
}

function edgesIntersect(
  left: readonly L.LatLng[],
  right: readonly L.LatLng[],
): boolean {
  for (let i = 0; i < left.length; i++) {
    const a = left[i];
    const b = left[(i + 1) % left.length];
    for (let j = 0; j < right.length; j++) {
      const c = right[j];
      const d = right[(j + 1) % right.length];
      if (segmentsIntersect(a, b, c, d)) return true;
    }
  }
  return false;
}

function segmentsIntersect(
  a: L.LatLng,
  b: L.LatLng,
  c: L.LatLng,
  d: L.LatLng,
): boolean {
  const cross = (p: L.LatLng, q: L.LatLng, r: L.LatLng) =>
    (q.lng - p.lng) * (r.lat - p.lat)
    - (q.lat - p.lat) * (r.lng - p.lng);
  const abC = cross(a, b, c);
  const abD = cross(a, b, d);
  const cdA = cross(c, d, a);
  const cdB = cross(c, d, b);
  const epsilon = 1e-12;
  const onSegment = (p: L.LatLng, q: L.LatLng, r: L.LatLng) =>
    q.lat >= Math.min(p.lat, r.lat) - epsilon
    && q.lat <= Math.max(p.lat, r.lat) + epsilon
    && q.lng >= Math.min(p.lng, r.lng) - epsilon
    && q.lng <= Math.max(p.lng, r.lng) + epsilon;

  if (Math.abs(abC) <= epsilon && onSegment(a, c, b)) return true;
  if (Math.abs(abD) <= epsilon && onSegment(a, d, b)) return true;
  if (Math.abs(cdA) <= epsilon && onSegment(c, a, d)) return true;
  if (Math.abs(cdB) <= epsilon && onSegment(c, b, d)) return true;
  return Math.sign(abC) !== Math.sign(abD)
    && Math.sign(cdA) !== Math.sign(cdB);
}
